<!-- 
Providers for a levy collection.
-->
<template>
  <div>
    <our-heading 
      :breadcrumbs="getBreadcrumbs('providers')"
      :subtitle="statusDescription"
      title="Providers"
      subtitle-tooltip="Levy collection status"
      user-guide-topic="levy-collection-providers"
    >
      <template v-slot:navigator>
        <levy-collection-navigator :levy-collection-id="levyCollectionId" :sandbox="isSandbox" />
      </template>
    </our-heading>
    <our-search-bar v-if="hasProviders">
      <our-quick-search 
        slot="quick-search"
        :criteria-mutation="criteriaMutation" 
        :value="quickSearch"
        label="Provider name or code" 
        field="name" 
        field-if-numeric="providerCode" 
        @changed="$refs.providers.refresh()"
      />
      <our-search-button 
        slot="search-button"
        :criteria="criteria" 
        :criteria-mutation="criteriaMutation" 
        :search-filters="searchFilters" 
        title="Search levy collection providers"
        @changed="$refs.providers.refresh()"
      />
      <our-saved-searches-menu
        slot="saved-searches-button"
        :criteria="criteria"
        :criteria-mutation="criteriaMutation"
        :clear-criteria-mutation="clearCriteriaMutation"
        module="levyCollectionProviders" 
        @selected="$refs.providers.refresh()"
      />
      <warnings-menu slot="middle" :levy-collection-id="levyCollectionId" />
      <v-btn 
        v-if="canAddProvider" 
        slot="right" 
        :block="xs" 
        class="mb-3" 
        @click="showAddProviderDialog = true"
      >
        <v-icon class="mr-2">playlist_add</v-icon>
        Add provider
      </v-btn>
    </our-search-bar>
    <levy-collection-provider-listing
      v-if="!loading"
      v-show="hasProviders" 
      ref="providers" 
      :levy-collection-id="levyCollectionId"
      :levy-collection-status="levyCollection.status"
      @click="openProvider"
    />
    <our-getting-started-hint v-if="!hasProviders" :levy-collection-id="levyCollectionId">
      Go to <i>Workflow</i> and run the <i>Import in-scope providers</i> task to get started.
    </our-getting-started-hint>
    <add-provider-dialog v-model="showAddProviderDialog" :levy-collection-id="levyCollectionId"/>
  </div>
</template>

<script>
import _ from "underscore";
import mixins from "./mixins";
import overrideMixins from "@/mixins/override.mixins";
import LevyCollectionNavigator from "@/components/levy-collections/LevyCollectionNavigator.vue";
import WarningsMenu from "@/components/levy-collections/WarningsMenu.vue";
import LevyCollectionProviderListing from "@/components/levy-collection-providers/LevyCollectionProviderListing.vue";
import AddProviderDialog from "@/components/levy-collection-overrides/AddProviderDialog.vue";
import * as mutation from "@/store/levy-collection-providers/mutation-types";
import { getSearchFilters } from "@/store/levy-collection-providers/helpers";

export default {
  components: {
    LevyCollectionNavigator,
    WarningsMenu,
    LevyCollectionProviderListing,
    AddProviderDialog
  },
  mixins: [mixins, overrideMixins],
  data() {
    return {
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      clearCriteriaMutation: mutation.LIST_CLEAR_CRITERIA,
      showAddProviderDialog: false
    };
  },
  computed: {
    hasProviders() {
      return !this.$store.state.levyCollectionProviders.listing.empty;
    },
    canAddProvider() {
      return this.canEditOverrides(null);
    },
    criteria() {
      return this.$store.state.levyCollectionProviders.listing.criteria;
    },
    quickSearch() {
      return this.criteria.providerCode && !this.criteria.name ? this.criteria.providerCode : this.criteria.name;
    },
    searchFilters() {
      const exclusions = this.isSandbox
        ? [
            "levyCollectionProviderStatus",
            "levyCollectionProviderWarningTypes",
            "overrideCategories",
            "levyCollectionProviderStatus",
            "paymentStatus",
            "levyCollectionProviderWarningTypes",
            "overrideCategories",
            "hasNotesForLevyCollection",
            "manuallyFinalised",
            "manuallyFinalisedReasons",
            "hasNotes"
          ]
        : [];
      if (!this.levyCollection.enableLevyCap) exclusions.push("levyCapApplies");
      if (!this.levyCollection.enableSanctions) exclusions.push("sanctioned");
      if (!this.levyCollection.enableShortRegistrationPeriodRiskFactor) exclusions.push("shortRegistrationPeriod");
      return _(getSearchFilters()).filter(f => !exclusions.includes(f.value));
    },
    xs() {
      return this.$vuetify.breakpoint.xsOnly;
    }
  },
  methods: {
    openProvider(providerId) {
      this.$router.push({ path: `/levy-collections/${this.levyCollectionId}/providers/${providerId}` });
    }
  }
};
</script>
